import React, { useState , useEffect, useRef} from 'react';
import { useAuth } from '../hooks/useAuth'; // Adjust the path as necessary
import qquest_icon from "../assets/logo.png";
import { useNavigate,useLocation } from 'react-router-dom';
import firebase from '../firebase/firebaseConfig';

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [requiresMFA, setRequiresMFA] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationId, setVerificationId] = useState("");
    const [mfaStep, setMfaStep] = useState('phone'); // 'phone' or 'code'
    const recaptchaVerifierRef = useRef(null);
    const recaptchaContainerRef = useRef(null);
    const navigate = useNavigate(); 
    const { signIn, verifyMFASignIn } = useAuth(); // Using the signIn method from context
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const emailParam = params.get('email');
        if (emailParam) {
            setEmail(emailParam);
        }
    }, [location]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const result = await signIn(email, password); // Use the signIn method from the context
            
            if (result.requiresMFA) {
                setRequiresMFA(true);
                setMfaResolver(result.resolver);
                setLoginError("");
                // Wait for the MFA form to render, then send verification code
                // Pass the resolver directly to avoid state update timing issues
                setTimeout(() => {
                    handleSendVerificationCode(null, result.resolver);
                }, 100);
            } else {
                setLoginError(""); // Clear any previous errors
            }
        } catch (error) {
            setLoginError(error.message); // Handle errors such as invalid credentials
        }
    };

    const handleSendVerificationCode = async (e, resolverParam) => {
        if (e) e.preventDefault();
        setLoginError("");
        
        try {
            // Use the passed resolver parameter or fall back to state
            const resolver = resolverParam || mfaResolver;
            if (!resolver) {
                throw new Error('MFA resolver not available');
            }

            // Ensure the container exists
            if (!recaptchaContainerRef.current) {
                throw new Error('reCAPTCHA container not found');
            }

            // Always create a fresh verifier for each attempt
            if (recaptchaVerifierRef.current) {
                try {
                    recaptchaVerifierRef.current.clear();
                } catch (e) {
                    // Ignore cleanup errors
                }
                recaptchaVerifierRef.current = null;
            }

            // Clear any existing reCAPTCHA widgets
            try {
                while (recaptchaContainerRef.current.firstChild) {
                    recaptchaContainerRef.current.removeChild(recaptchaContainerRef.current.firstChild);
                }
            } catch (e) {
                try {
                    recaptchaContainerRef.current.innerHTML = '';
                } catch (e2) {
                    console.warn('Could not clear reCAPTCHA container:', e2);
                }
            }

            // Create new reCAPTCHA verifier
            recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier(
                recaptchaContainerRef.current,
                {
                    size: 'invisible',
                    callback: () => {
                        // reCAPTCHA solved
                    },
                    'expired-callback': () => {
                        setLoginError('reCAPTCHA expired. Please try again.');
                    }
                }
            );

            // Render the reCAPTCHA
            await Promise.race([
                recaptchaVerifierRef.current.render(),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('reCAPTCHA render timeout')), 15000)
                )
            ]);

            // Wait for reCAPTCHA to fully initialize
            await new Promise(resolve => setTimeout(resolve, 1000));

            if (!recaptchaVerifierRef.current) {
                throw new Error('reCAPTCHA not initialized');
            }

            // Get the enrolled MFA factors from the resolver
            const enrolledFactors = resolver.hints;
            if (!enrolledFactors || enrolledFactors.length === 0) {
                throw new Error('No enrolled MFA factors found');
            }

            // For MFA sign-in, Firebase masks the phone number in hints for privacy
            // We should use multiFactorHint with the resolver session instead of extracting the phone number
            const multiFactorHint = enrolledFactors[0];
            
            // Store the masked phone number for display purposes
            setPhoneNumber(multiFactorHint.phoneNumber || 'Phone Number');

            // Get the session from the resolver
            let session;
            try {
                if (typeof resolver.session === 'function') {
                    session = await Promise.race([
                        resolver.session(),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error('Session request timeout')), 10000)
                        )
                    ]);
                } else if (resolver.session) {
                    session = resolver.session;
                } else {
                    throw new Error('Resolver session not available. This is required for MFA sign-in verification.');
                }
            } catch (sessionError) {
                let sessionErrorMessage = sessionError.message || 'Unknown error';
                if (sessionError.message === 'Session request timeout' || sessionError.message === 'Timeout') {
                    sessionErrorMessage = 'Failed to get MFA session. Please try signing in again.';
                }
                throw new Error(sessionErrorMessage);
            }

            // Send verification code using multiFactorHint and session
            // This is the correct Firebase approach for MFA sign-in
            const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
            const verificationId = await Promise.race([
                phoneAuthProvider.verifyPhoneNumber({
                    multiFactorHint: multiFactorHint,
                    session: session
                }, recaptchaVerifierRef.current),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Verification request timeout')), 30000)
                )
            ]);

            setVerificationId(verificationId);
            setMfaStep('code');
        } catch (error) {
            // Clean up verifier on error
            if (recaptchaVerifierRef.current) {
                try {
                    recaptchaVerifierRef.current.clear();
                } catch (e) {
                    // Ignore cleanup errors
                }
                recaptchaVerifierRef.current = null;
            }

            let errorMessage = error.message || "Failed to send verification code";
            if (error.code === 'auth/operation-not-allowed') {
                errorMessage = 'Phone authentication is not enabled in Firebase Console. Please contact your administrator.';
            } else if (error.code === 'auth/invalid-phone-number') {
                errorMessage = 'Invalid phone number format.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many requests. Please try again later.';
            } else if (error.message && error.message.includes('timeout')) {
                errorMessage = 'Request is taking too long. Please check your internet connection and try again.';
            }
            setLoginError(errorMessage);
        }
    };

    const handleVerifyMFA = async (e) => {
        e.preventDefault();
        setLoginError("");
        
        try {
            if (!mfaResolver || !verificationId) {
                throw new Error('MFA resolver or verification ID not available');
            }

            await verifyMFASignIn(mfaResolver, verificationCode, verificationId);
            setLoginError("");
            
            // Clean up reCAPTCHA
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
                recaptchaVerifierRef.current = null;
            }
        } catch (error) {
            let errorMessage = error.message || "Failed to verify MFA code";
            if (error.code === 'auth/invalid-verification-code') {
                errorMessage = 'Invalid verification code. Please check and try again.';
            } else if (error.code === 'auth/code-expired') {
                errorMessage = 'Verification code has expired. Please request a new code.';
            }
            setLoginError(errorMessage);
        }
    };


    if (requiresMFA) {
        return (
            <div className="flex items-center w-full mx-auto h-screen bg-[#FAFAFA]">
                <form onSubmit={handleVerifyMFA} className="grid place-items-center lg:w-5/12 sm:w-9/12 w-11/12 mx-auto bg-white text-gray-800 shadow-2xl rounded-3xl">
                    <div className='flex items-center mb-4'>
                        <img src={qquest_icon} alt='Qquest' className='w-12 pt-16 pb-4 mr-4'/>
                        <div className="pt-16 pb-4 text-3xl font-bold capitalize logo text-black">Enter Verification Code</div>
                    </div>
                    
                    {phoneNumber && (
                        <div className="w-full flex items-center px-14 pb-4">
                            <p className='text-sm text-gray-600'>Verification code sent to {phoneNumber}</p>
                        </div>
                    )}
                    
                    <div className="w-full flex items-center px-14 pb-6">
                        <label className='flex-shrink-0 text-sm font-medium text-gray-800 mt-1 mr-12'>Verification Code</label>
                        <input
                            type="text"
                            className="ml-4 mt-1 px-2 py-1 text-sm flex-1 border rounded-md mr-auto shadow-sm max-w-[542px]"
                            placeholder="123456"
                            required
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                        />
                    </div>
                    {loginError && <div className="px-8" style={{ color: 'red', textAlign: 'center' }}>{loginError}</div>}
                    <div 
                        id="recaptcha-container" 
                        ref={recaptchaContainerRef} 
                        style={{ 
                            position: 'absolute', 
                            left: '-9999px', 
                            visibility: 'hidden',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden'
                        }}
                        aria-hidden="true"
                    ></div>
                    <div className="mx-auto flex justify-center items-center pt-6 pb-6">
                        <button
                            type="submit"
                            className="flex items-center justify-center bg-[#F58F29] border border-gray-200 p-1 mt-1 mb-1 rounded-md shadow w-[200px]"
                        >
                            <p className="text-white">Verify</p>
                        </button>
                    </div>
                    <div className="mx-auto flex justify-center items-center pb-16">
                        <div className="text-sm text-blue-600 cursor-pointer hover:underline mx-3" onClick={async () => {
                            setVerificationCode("");
                            setVerificationId("");
                            await handleSendVerificationCode();
                        }}>Resend Code</div>
                        <p className="text-sm text-gray-800 ">|</p>
                        <div className="text-sm text-blue-600 cursor-pointer hover:underline mx-3" onClick={() => {
                            setRequiresMFA(false);
                            setMfaResolver(null);
                            setPhoneNumber("");
                            setVerificationCode("");
                            setVerificationId("");
                            if (recaptchaVerifierRef.current) {
                                recaptchaVerifierRef.current.clear();
                                recaptchaVerifierRef.current = null;
                            }
                        }}>Back to Login</div>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="flex items-center w-full mx-auto h-screen bg-[#FAFAFA]">
            <form onSubmit={handleSubmit} className="grid place-items-center lg:w-5/12 sm:w-9/12 w-11/12 mx-auto bg-white text-gray-800 shadow-2xl rounded-3xl">
                <div className='flex items-center mb-4'>
                  <img src={qquest_icon} alt='Qquest' className='w-12 pt-16 pb-4 mr-4'/>
                  <div className="pt-16 pb-4 text-3xl font-bold capitalize logo text-black">Login To Qquest</div>
                </div>
                
                <div className="w-full flex items-center px-14 pb-6">
                    <label className='flex-shrink-0 text-sm font-medium text-gray-800 mt-1 mr-20'>Email</label>
                    <input
                        type="email"
                        className="ml-4 mt-1 px-2 py-1 text-sm flex-1 border rounded-md mr-auto shadow-sm max-w-[542px]"
                        placeholder="example@123.com"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div className="w-full flex items-center px-14 pb-4">
                    <label className='flex-shrink-0 text-sm font-medium text-gray-800 mt-1 mr-12'>Password</label>
                    <input
                        type="password"
                        className="ml-4 mt-1 px-2 py-1 text-sm flex-1 border rounded-md mr-auto shadow-sm max-w-[542px]"
                        placeholder="********"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>
                {loginError && <div className = "px-8" style={{ color: 'red', textAlign: 'center' }}>{loginError}</div>}
                <div className="mx-auto flex justify-center items-center pt-6 pb-6">
                    <button
                        type="submit"
                        className="flex items-center justify-center bg-[#F58F29] border border-gray-200 p-1 mt-1 mb-1 rounded-md shadow w-[200px]"
                    >
                        <p className="text-white">Login</p>
                    </button>
                </div>
                <div className="mx-auto flex justify-center items-center pb-16">
                  <div className="text-sm text-blue-600 cursor-pointer hover:underline mx-3" onClick={() => navigate("/forget-password")}>Forget password</div>
                  <p className="text-sm text-gray-800 ">|</p>
                  <div className="text-sm text-blue-600 cursor-pointer hover:underline mx-3" onClick={() => navigate("/register")}>Register to Qquest</div>
   
                </div>
            </form>
        </div>
    );
};

export default Login;
